import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Checkbox, FormControlLabel, IconButton, Link, MenuItem, Skeleton,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { C, Pill, fecha } from "../oece/oeceUi";
import {
  getLeads, guardarContacto, Lead, LeadDenunciado,
} from "../services/notificaciones.service";

/* Pastilla del plazo de descargos (estimado) */
const Plazo = ({ lead }: { lead: Lead }) => {
  const d = lead.dias_habiles_restantes;
  if (d === null || !lead.vence_aprox) return <Typography component="span" sx={{ color: C.muted }}>—</Typography>;
  const tono = d < 0 ? "gray" : d <= 3 ? "red" : d <= 6 ? "amber" : "green";
  return (
    <Tooltip title="Estimado: notificación en casilla al día hábil siguiente + 10 días hábiles. Confirmar en el decreto.">
      <Box>
        <Pill tono={tono}>{d < 0 ? "Vencido" : d === 0 ? "Vence hoy" : `${d} días háb.`}</Pill>
        <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 0.4 }}>~ {fecha(lead.vence_aprox)}</Typography>
      </Box>
    </Tooltip>
  );
};

/* Teléfono + nota de un denunciado; se guarda al salir del campo o con Enter.
   Para el abogado (esAbogado) el teléfono es de solo lectura: solo edita la nota. */
const ContactoEditable = ({ d, esAbogado, onGuardado }: { d: LeadDenunciado; esAbogado: boolean; onGuardado: (ruc: string, tel: string, nota: string) => void }) => {
  const tel = d.contacto?.telefono ?? "";
  const [telEdit, setTelEdit] = useState(tel);
  const [nota, setNota] = useState(d.contacto?.nota ?? "");
  const [estado, setEstado] = useState<"" | "guardando" | "ok" | "error">("");
  const ultimo = useRef({ tel, nota: d.contacto?.nota ?? "" });

  if (!d.ruc) {
    return <Typography sx={{ fontSize: 12, color: C.muted }}>Sin RUC en el decreto</Typography>;
  }

  const guardar = () => {
    const telActual = esAbogado ? tel : telEdit;
    if (telActual === ultimo.current.tel && nota === ultimo.current.nota) return;
    setEstado("guardando");
    guardarContacto(d.ruc!, { telefono: telActual, nota })
      .then(() => {
        ultimo.current = { tel: telActual, nota };
        onGuardado(d.ruc!, telActual, nota);
        setEstado("ok");
        setTimeout(() => setEstado(""), 2000);
      })
      .catch(() => setEstado("error"));
  };
  const alEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  };

  return (
    <Stack spacing={0.6} sx={{ minWidth: 190 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {esAbogado ? (
          <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.navy }}>
            {tel || <Typography component="span" sx={{ color: C.muted, fontWeight: 400 }}>Sin teléfono</Typography>}
          </Typography>
        ) : (
          <TextField
            size="small" placeholder="Teléfono" value={telEdit}
            onChange={(e) => setTelEdit(e.target.value)} onBlur={guardar} onKeyDown={alEnter}
            inputProps={{ style: { fontSize: 13, padding: "6px 8px" } }} sx={{ flex: 1 }}
          />
        )}
        {estado === "ok" && <CheckCircleIcon sx={{ fontSize: 18, color: C.green }} />}
      </Stack>
      <TextField
        size="small" placeholder="Nota (opcional)" value={nota}
        onChange={(e) => setNota(e.target.value)} onBlur={guardar} onKeyDown={alEnter}
        inputProps={{ style: { fontSize: 12, padding: "5px 8px" } }}
      />
      {estado === "guardando" && <Typography sx={{ fontSize: 11, color: C.muted }}>Guardando…</Typography>}
      {estado === "error" && <Typography sx={{ fontSize: 11, color: C.red }}>No se pudo guardar</Typography>}
    </Stack>
  );
};

export const LeadsTab = ({ esAbogado = false }: { esAbogado?: boolean }) => {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [tipoRuc, setTipoRuc] = useState<"" | "10" | "20">("");
  const [soloVigentes, setSoloVigentes] = useState(true);
  const [soloSinContacto, setSoloSinContacto] = useState(false);
  const [q, setQ] = useState("");
  const [copiado, setCopiado] = useState("");
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cargar = useCallback(() => {
    getLeads({ q: q || undefined, tipo_ruc: tipoRuc || undefined })
      .then((r) => setLeads(r.data))
      .catch(() => {});
  }, [q, tipoRuc]);

  useEffect(() => { cargar(); }, [cargar]);

  const onBuscar = (v: string) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQ(v), 300);
  };

  // Al guardar un contacto lo reflejamos en todas las filas con ese RUC
  const onGuardado = (ruc: string, telefono: string, nota: string) => {
    setLeads((prev) => prev?.map((l) => ({
      ...l,
      denunciados: l.denunciados.map((d) => d.ruc === ruc
        ? { ...d, contacto: { ruc, telefono, nota, actualizado_en: new Date().toISOString() } }
        : d),
    })) ?? null);
  };

  const copiar = (ruc: string) => {
    navigator.clipboard?.writeText(ruc);
    setCopiado(ruc);
    setTimeout(() => setCopiado(""), 1500);
  };

  const visibles = useMemo(() => (leads ?? []).filter((l) =>
    (!soloVigentes || (l.dias_habiles_restantes ?? -1) >= 0) &&
    (!soloSinContacto || l.denunciados.some((d) => d.ruc && !d.contacto?.telefono)),
  ), [leads, soloVigentes, soloSinContacto]);

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Tipo de RUC" value={tipoRuc}
          onChange={(e) => setTipoRuc(e.target.value as "" | "10" | "20")} sx={{ minWidth: 210 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="10">RUC 10 · Persona natural</MenuItem>
          <MenuItem value="20">RUC 20 · Empresa</MenuItem>
        </TextField>
        <FormControlLabel
          control={<Checkbox checked={soloVigentes} onChange={(e) => setSoloVigentes(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>Solo con plazo vigente</Typography>}
        />
        {!esAbogado && (
          <FormControlLabel
            control={<Checkbox checked={soloSinContacto} onChange={(e) => setSoloSinContacto(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>Solo sin teléfono</Typography>}
          />
        )}
        <TextField
          size="small" type="search" placeholder="Buscar denunciado, RUC, entidad o expediente…"
          onChange={(e) => onBuscar(e.target.value)} sx={{ flex: 1, minWidth: 260 }}
        />
      </Stack>

      <Box sx={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 2 }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { px: 1.5, py: 1.25, textAlign: "left", borderBottom: `1px solid ${C.border}`, fontSize: 13.5, verticalAlign: "top" }, "& th": { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: C.muted, bgcolor: "#f8fafc" } }}>
          <thead>
            <tr>
              <th>Plazo</th><th>Expediente</th><th>Denunciado</th><th>RUC</th>
              <th>Contacto</th><th>Entidad</th><th>Infracción</th><th>PDF</th>
            </tr>
          </thead>
          <tbody>
            {leads === null && [0, 1, 2, 3, 4].map((i) => (
              <tr key={i}><td colSpan={8}><Skeleton height={22} /></td></tr>
            ))}
            {leads !== null && visibles.length === 0 && (
              <tr><td colSpan={8}><Typography sx={{ py: 3, textAlign: "center", color: C.muted }}>Sin leads con estos filtros.</Typography></td></tr>
            )}
            {visibles.flatMap((l) => {
              const dens = l.denunciados.length ? l.denunciados : [{ nombre: "—", ruc: null, dni: null, contacto: null }];
              return dens.map((d, i) => (
                <tr key={`${l.idnotifica}-${d.ruc ?? i}`} style={i < dens.length - 1 ? { borderBottom: "none" } : undefined}>
                  {i === 0 && (
                    <>
                      <td rowSpan={dens.length}><Plazo lead={l} /></td>
                      <td rowSpan={dens.length}>
                        <Typography sx={{ fontWeight: 700, color: C.navy, fontSize: 13.5 }}>{l.n_expediente || "—"}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: C.muted }}>
                          Decreto {l.n_decreto || "—"} · {fecha(l.fecha_decreto)}
                        </Typography>
                        {dens.length > 1 && <Pill tono="gold">{`${dens.length} denunciados`}</Pill>}
                      </td>
                    </>
                  )}
                  <td style={{ fontWeight: 600, maxWidth: 280 }}>{d.nombre}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {d.ruc ? (
                      <Stack direction="row" alignItems="center" spacing={0.3}>
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>{d.ruc}</span>
                        <Tooltip title={copiado === d.ruc ? "¡Copiado!" : "Copiar RUC"}>
                          <IconButton size="small" onClick={() => copiar(d.ruc!)}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton>
                        </Tooltip>
                      </Stack>
                    ) : "—"}
                    {d.ruc && <Pill tono={d.ruc.startsWith("10") ? "blue" : "gray"}>{d.ruc.startsWith("10") ? "Persona" : "Empresa"}</Pill>}
                  </td>
                  <td><ContactoEditable key={d.ruc ?? i} d={d} esAbogado={esAbogado} onGuardado={onGuardado} /></td>
                  {i === 0 && (
                    <>
                      <td rowSpan={dens.length} style={{ maxWidth: 220 }}>{l.entidad || "—"}</td>
                      <td rowSpan={dens.length} style={{ maxWidth: 220, color: "#334155" }}>{l.infraccion || "—"}</td>
                      <td rowSpan={dens.length}>
                        {l.url_pdf ? (
                          <Link href={l.url_pdf} target="_blank" rel="noopener" sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.3 }}>
                            ver PDF <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        ) : "—"}
                      </td>
                    </>
                  )}
                </tr>
              ));
            })}
          </tbody>
        </Box>
      </Box>
      <Typography sx={{ pt: 1.5, color: C.muted, fontSize: 12 }}>
        {leads ? `${visibles.length} lead(s) de ${leads.length} decretos de inicio · más recientes primero · el plazo es estimado` : "Cargando…"}
      </Typography>
    </>
  );
};
